import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { registerLocaleData } from '@angular/common';
import localeHr from '@angular/common/locales/hr';
import localeHrExtra from '@angular/common/locales/extra/hr';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';

import { HeaderComponent } from './components/header/header.component';
import { FooterComponent } from './components/footer/footer.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { MessageBoxComponent } from './components/message-box/message-box.component';
import { ModalConfirmationComponent } from './components/modal-confirmation/modal-confirmation.component';
import { FilterComponent } from 'src/app/shared/components/filter/filter.component';
import { PaginatorComponent } from './components/paginator/paginator.component';
import { ModalSelectComponent } from './components/modal-select/modal-select.component';
import { UsersFilterPipe } from './pipes/users-filter.pipe';
import { UsersWithEmailPipe } from './pipes/users-with-email.pipe';
import { TextSizeFilterPipe } from './pipes/text-size-filter.pipe';
import { TextSizeFilterWhitespacePipe } from './pipes/text-size-filter-whitespace.pipe';
import { AfterValueChangedDirective } from './directives/after-value-changed.directive';
import { OwlDateTimeModule, OwlNativeDateTimeModule, OWL_DATE_TIME_LOCALE } from '@danielmoncada/angular-datetime-picker';
import { IonicModule } from '@ionic/angular';
import { GetControllersForRoomsOnFloorPipe } from './pipes/get-controllers-for-rooms-on-floor.pipe';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { AlarmSubscriptionFilterPipe } from './pipes/alarm-subscription-filter.pipe';
import { CustomTranslatePipe } from './pipes/custom-translate.pipe';
import { EditLocationModalComponent } from './components/edit-location-modal/edit-location-modal.component';
import { HasEveryAccessGuestRoomPermissionPipe } from './pipes/user-has-every-access-guest-room-permission.pipe';
import { HasEveryAccessGuestRoomPermissionInRolePipe } from './pipes/has-every-access-guest-room-permission-in-role.pipe';
import { GetObjectsPipe } from './pipes/get-objects.pipe';
import { GetSubobjectsPipe } from './pipes/get-subobjects.pipe';
import { GetUserForCardPipe } from './pipes/get-user-for-card.pipe';
import { GetCardTypeCodePipe } from './pipes/get-card-type-code.pipe';
import { GetCardTypeNameByCodePipe } from './pipes/get-card-type-name-by-code.pipe';
import { AccessLogsComponent } from './../modules/logs/pages/access-logs/access-logs.component';
import { DateTimeFromToComponent } from '../modules/logs/components/date-time-from-to/date-time-from-to.component';
import { AuditComponent } from '../modules/logs/pages/audit/audit.component';
import { AlarmLogsComponent } from '../modules/logs/pages/alarm-logs/alarm-logs.component';
import { ControllerIsInListPipe } from './pipes/controller-is-in-list.pipe';
import { FindPropertyByCodePipe } from './pipes/find-property-by-code.pipe';
import { PropertiesAreActivePipe } from './pipes/properties-are-active.pipe';
import { StatusIsActivePipe } from './pipes/status-is-active.pipe';
import { RoomLogPaginatorComponent } from './components/room-log-paginator/room-log-paginator.component';
import { TranslateModule } from '@ngx-translate/core';
import { AccessControlCardComponent } from '../modules/corridors/components/access-control-card/access-control-card.component';
import { RoomModalComponent } from './components/room-modal/room-modal.component';
import { RoomModalCardsComponent } from './components/room-modal-cards/room-modal-cards.component';
import { RoomModalLogComponent } from './components/room-modal-log/room-modal-log.component';
import { RoomModalServiceComponent } from './components/room-modal-service/room-modal-service.component';
import { RoomModalSettingsComponent } from './components/room-modal-settings/room-modal-settings.component';
import { RoomModalViewComponent } from './components/room-modal-view/room-modal-view.component';
import { BooleanInputComponent } from './components/boolean-input/boolean-input.component';
import { EditControllerComponent } from './components/edit-controller/edit-controller.component';
import { FindPropertyPipe } from './pipes/find-property.pipe';
import { GetActivePropsPipe } from './pipes/get-active-props.pipe';
import { GetFirstControllerWithPropertyPipe } from './pipes/get-first-controller-with-property.pipe';
import { GetFloorsForEditingGroupsPipe } from './pipes/get-floors-for-editing-groups.pipe';
import { GetRoomStatusColorPipe } from './pipes/get-room-status-color.pipe';
import { GetRoomsOnFloorPipe } from './pipes/get-rooms-on-floor.pipe';
import { PropIsActivePipe } from './pipes/prop-is-active.pipe';
import { TransformFloorsPipe } from './pipes/transform-floors.pipe';
import { FilterGroupsForSubobjectPipe } from './pipes/filter-groups-for-subobject.pipe';
import { DragToSelectModule } from 'ngx-drag-to-select';
import { SelectTimeComponent } from './components/select-time/select-time.component';
import { NotFoundComponent } from './equipment/not-found/not-found.component';
import { FilterByControllerType } from './pipes/filter-by-controller-type.pipe';
import { IOCommonAreaCardComponent } from '../modules/corridors/components/io-common-area-card/io-common-area-card.component';
import { AccessControlModalCardsComponent } from '../modules/corridors/components/access-control-area-modals/access-control-modal-cards/access-control-modal-cards.component';
import { AccessControlModalLogComponent } from '../modules/corridors/components/access-control-area-modals/access-control-modal-log/access-control-modal-log.component';
import { AccessControlModalSettingsComponent } from '../modules/corridors/components/access-control-area-modals/access-control-modal-settings/access-control-modal-settings.component';
import { IOCommonAreaModalLogComponent } from '../modules/corridors/components/io-common-area-modals/io-common-area-modal-log/io-common-area-modal-log.component';
import { IOCommonAreaModalSettingsComponent } from '../modules/corridors/components/io-common-area-modals/io-common-area-modal-settings/io-common-area-modal-settings.component';
import { SelectTimeCommonRoomComponent } from './components/select-time-common-room/select-time-common-room.component';
import { HvacCommonAreaCardComponent } from '../modules/corridors/components/hvac-common-area-card/hvac-common-area-card.component';
import { HvacCommonAreaModalViewComponent } from '../modules/corridors/components/hvac-common-area-modals/hvac-common-area-modal-view/hvac-common-area-modal-view.component';
import { HvacCommonAreaModalLogComponent } from '../modules/corridors/components/hvac-common-area-modals/hvac-common-area-modal-log/hvac-common-area-modal-log.component';
import { OutputModeCopyPasteComponent } from './components/output-mode-copy-paste/output-mode-copy-paste.component';
import { UserHasPermissionPipe } from '../modules/users/pipes/user-has-permission.pipe';
// import { RoomModalFacilityComponent } from '../modules/rooms/components/room-modal-facility/room-modal-facility.component';
import { DisplayPropertyComponent } from './components/display-property/display-property.component';
import { StickyHeaderDirectiveCommonArea } from './directives/track-scroll-common-area.directive';
import { StickyHeaderDirectiveRooms } from './directives/track-scroll-rooms.directive';
import { FilterByGroupTypePipe } from './pipes/filter-by-group-type.pipe';
import { GroupSettingsComponent } from '../modules/rooms/components/group-settings/group-settings.component';
import { HvacModeModalComponent } from '../modules/rooms/components/hvac-mode-modal/hvac-mode-modal.component';
import { ShowSubobjectPipe } from './pipes/show-subobject.pipe';
import { FilterHvacModesPipe } from './pipes/filter-hvac-modes.pipe';
import { GetRolePipe } from './pipes/get-role.pipe';
import { GetObjectsByIdsPipe } from './pipes/get-objects-by-ids.pipe';
import { SearchByNamePipe } from './pipes/search-by-name.pipe';
import { SelectDaysModalComponent } from '../modules/automation/components/select-days-modal/select-days-modal.component';
import { TimeConditionComponent } from '../modules/automation/components/time-condition/time-condition.component';
import { GetFloorsForControllerSelectionPipe } from './pipes/get-floors-for-controller-selection.pipe';
import { GetAccessControllsOnFloorPipe } from './pipes/get-access-controlls-on-floor.pipe';
import { GetFloorsForRoomSelectionPipe } from './pipes/get-floors-for-room-selection.pipe';
import { GetRoomsOnFloorForSelectionPipe } from './pipes/get-rooms-on-floor-for-selection.pipe';
import { GetPresetNamePipe } from './pipes/get-preset-name.pipe';
import { PropExistInControllerPipe } from './pipes/prop-exist-in-controller.pipe';
import { GetFloorsInSubobjectPipe } from './pipes/get-floors-in-subobject.pipe';
import { PropExistInControllersPipe } from './pipes/prop-exist-in-controllers.pipe';
import { CanIntervalBeSetPipe } from '../modules/corridors/pipes/can-interval-be-set.pipe';
import { OutputModeForLightIsControlPipe } from '../modules/corridors/pipes/output-mode-for-light-is-control.pipe';
import { IsAccessControlPipe } from '../modules/rooms/pipes/is-access-control.pipe';
import { IsPermissionAccessControl } from './pipes/is-permission-access-control.pipe';
import { UserHavePermissionForAnyAlarmsPipe } from '../modules/users/pipes/user-have-permission-for-any-alarms.pipe';
import { IsHvacCommonAreaPipe } from '../modules/rooms/pipes/is-hvac-common-area.pipe';
import { IsGuestRoomPipe } from '../modules/rooms/pipes/is-guest-room.pipe';
import { IsIoCommonAreaPipe } from '../modules/rooms/pipes/is-io-common-area.pipe';
import { DateTimeInlineComponent } from './components/date-time-inline/date-time-inline.component';
import { ModalViewBooleanPropComponent } from './components/modal-view-boolean-prop/modal-view-boolean-prop.component';
import { ModalViewNumericPropComponent } from './components/modal-view-numeric-prop/modal-view-numeric-prop.component';
import { GetLatestHighestPriorityAlarmPipe } from './pipes/get-latest-highest-priority-alarm.pipe';
import { ButtonsSelectComponent } from './components/buttons-select/buttons-select.component';
import { GetRoomTemperatureDataPipe } from './pipes/get-room-temperature-data.pipe';
import { RoomTempInfoComponent } from './components/room-temp-info/room-temp-info.component';
import { GetAlarmsLevelPipe } from './pipes/get-alarms-level.pipe';
import { GetUnconfirmedAlarmTextPipe } from './pipes/get-unconfirmed-alarm-text.pipe';
import { GetValidToCssClassPipe } from './pipes/get-valid-to-css-class.pipe';
import { RoomTagPopoverComponent } from './components/room-tag-popover/room-tag-popover.component';
import { TranslateValuePipe } from './pipes/translate-value.pipe';
import { TextIncludePipe } from './pipes/text-include-filter.pipe';
import { NumberInputCodeComponent } from './components/number-input-code/number-input-code.component';
import { SortByAdminPropsPipe } from './pipes/sort-by-admin-props.pipe';
import { ModalViewMutexBooleanPropComponent } from './components/modal-view-mutex-boolean-prop/modal-view-mutex-boolean-prop.component';
import { PropIsLoadingPipe } from './pipes/prop-is-loading.pipe';
import { PropIsHvacPipe } from './pipes/prop-is-hvac.pipe';
import { RetroModeControllerPositionPipe } from './pipes/retro-mode-controller-position.pipe';
import { RoomHaveBathroomPipe } from './pipes/room-have-bathroom.pipe';
import { FloorHaveAcLocations } from './pipes/floor-have-ac-locations.pipe';
import { FilterSelectComponent } from './components/filter-select/filter-select.component';
import { GetLocationGroupsByTypePipe } from './pipes/get-location-groups-by-type.pipe';
import { FilterLocationsWithPropertyPipe } from './pipes/filter-locations-with-property.pipe';
import { GetLocationsInSubObjectPipe } from './pipes/get-locations-in-subObject.pipe';
import { GetControllersInSubObjectPipe } from './pipes/get-controllers-in-subObject.pipe';
import { ConvertLoadingComponent } from './components/convert-loading/convert-loading.component';
import { GetValidFromToCssPipe } from './pipes/get-valid-from-to-css.pipe';
import { LocationHasTagPipe } from './pipes/location-has-tag.pipe';
import { CommonAreasExistInProject } from './pipes/common-areas-exist-in-project.pipe';
import { IncludesPipe } from './pipes/includes.pipe';
import { BleKeyQrCodeComponent } from './components/ble-key-qr-code/ble-key-qr-code.component';
import { RoomModalHvacComponent } from './components/room-modal-hvac/room-modal-hvac.component';
import { GetFancoilSpeedPropsPipe } from './pipes/get-fancoil-speed-props.pipe';
import { GetControllerByIndexPipe } from './pipes/get-controller-by-index.pipe';
import { GetRoomRadiatorStatusPipe } from './pipes/get-room-radiator-status.pipe';
import { GetBathroomRadiatorStatusPipe } from './pipes/get-bathroom-radiator-status.pipe';
import { GetRelayOutputStatusNamePipe } from './pipes/get-relay-output-status-name.pipe';
import { GetRelayOutputStatusNumberPipe } from './pipes/get-relay-output-status-number.pipe';
import { GetRelayOutputStatusStatePipe } from './pipes/get-relay-output-status-state.pipe';
import { GetRelayOutputByNumberPipe } from './pipes/get-relay-output-by-number.pipe';
import { ShowRadiatorPipe } from './pipes/show-radiator.pipe';
import { ShowRadiatorModeSelectorPipe } from './pipes/show-radiator-mode-selector.pipe';
import { GetRelayOutputNumbersPipe } from './pipes/get-relay-output-numbers.pipe';
import { ShowRelayOutputSelectorPipe } from './pipes/show-relay-output-selector.pipe';
import { GetAllRelayPropsFromControllerPipe } from './pipes/all-relay-props-in-controller.pipe';
import { IsBurglaryDetectedPropPipe } from './pipes/is-burglary-detected-prop.pipe';
import { GetBurglaryIncludeNamePipe } from './pipes/get-burglary-include-name.pipe';
import { ModalViewMultipleBooleanPropComponent } from './components/modal-view-multiple-boolean-prop/modal-view-multiple-boolean-prop.component';
import { GetTypeForPresetPropPipe } from './pipes/get-type-for-preset-prop.pipe';
import { GetSubtitleForPresetPropsPipe } from './pipes/get-subtitle-for-preset-props.pipe';
import { ShowDisplayPropPipe } from './pipes/show-display-prop.pipe';
import { AllPropsComponent } from './components/all-props/all-props.component';
import { ApplyToModalComponent } from './components/apply-to-modal/apply-to-modal.component';
import { ControllerNotPolledPipe } from './pipes/controller-not-polled.pipe';
import { GetDictionaryValuePipe } from './pipes/get-dictionary-value.pipe';
import { DisplayDictionaryPropInRoomOverviewComponent } from './components/display-dictionary-prop-in-room-overview/display-dictionary-prop-in-room-overview.component';
import { DisplayDictionaryPropInRoomModalComponent } from './components/display-dictionary-prop-in-room-modal/display-dictionary-prop-in-room-modal.component';
import { GetRelayOutputNamePipe } from './pipes/get-relay-output-name.pipe';
import { GetRelayOutputPropsOfSameTypePipe } from './pipes/get-relay-output-props-of-same-type.pipe';
import { HideHumidityAndCo2Pipe } from './pipes/hide-humidity-and-co2.pipe';
import { FilterTemperatureLogsPipe } from './pipes/filter-temperature-logs.pipe';

import { SortFanCoilSpeedPropsPipe } from './pipes/sort-fan-coil-speed-props.pipe';
import { GetLogValuePropsPipe } from './pipes/get-log-value-props.pipe';
import { GetLogTemperaturePropsPipe } from './pipes/get-log-temperature-props.pipe';
import { RunFunctionPipe } from './pipes/run-function.pipe';
import { NgApexchartsModule } from 'ng-apexcharts';
import { GetLogValuePropertyNamePipe } from './pipes/get-log-value-property-name.pipe';
import { GetControllersInObjectPipe } from '../modules/rooms/pipes/get-controllers-in-object.pipe';
import { GetAveragePropValuePipe } from '../modules/rooms/pipes/get-average-prop-value.pipe';
import { SubObjectHaveLocations } from './pipes/subobject-have-locations.pipe';
import { FilterControllerByObjectPipe } from './pipes/filter-by-object.pipe';
import { AllControllersInLocNotPollingPipe } from './pipes/all-controllers-in-loc-not-polling.pipe';
import { ModalViewHeatCoolAutoComponent } from './components/modal-view-heat-cool-auto/modal-view-heat-cool-auto.component';
import { DisplaySetTemperatureComponent } from './components/display-set-temperature/display-set-temperature.component';
import { ModalViewSetTemperatureComponent } from './components/modal-view-set-temperature/modal-view-set-temperature.component';
import { EnterRoomNumberModalComponent } from './components/enter-room-number-modal/enter-room-number-modal.component';
import { LocationDragSelectModalComponent } from './components/location-drag-select-modal/location-drag-select-modal.component';
import { SearchLocationAndController } from './pipes/searchLocationAndController.pipe';
import { ShowHvacColumnPipe } from './pipes/show-hvac-column.pipe';
import { SortByNamePipe } from './pipes/sort-by-name.pipe';
import { ModalViewWindowPropComponent } from './components/modal-view-window-prop/modal-view-window-prop.component';
import { ModalViewFancoilModeComponent } from './components/modal-view-fancoil-mode/modal-view-fancoil-mode.component';
import { DisplayWindowComponent } from './components/display-window/display-window.component';
import { GetSelectedControllersPipe } from './pipes/get-selected-controllers.pipe';
import { UpdatingComponent } from './components/updating/updating.component';
import { HealthCheckModalComponent } from './components/health-check-modal/health-check-modal.component';
import { ReversedCommunicationAccessControlExistsInProjectPipe }
 from './pipes/reversed-communication-access-control-exists-in-project.pipe';
import { DisplayFancoilModeInRoomOverviewComponent } from './components/display-fancoil-mode-in-room-overview/display-fancoil-mode-in-room-overview.component';
import { RelayOutputsListComponent } from './components/relay-outputs-list/relay-outputs-list.component';
import { FilterHiddenPipe } from './pipes/filter-hidden.pipe';
import { HasPermissionWithIdPipe } from './pipes/has-permission-with-id.pipe';
import { FloorHasAcLocationOrPassageRelayPipe } from './pipes/floor-has-ac-location-or-passage-relay.pipe';

registerLocaleData(localeHr, 'hr-HR', localeHrExtra);

@NgModule({
  declarations: [
    HeaderComponent,
    FooterComponent,
    SidebarComponent,
    MessageBoxComponent,
    ModalConfirmationComponent,
    FilterComponent,
    FilterSelectComponent,
    PaginatorComponent,
    UsersFilterPipe,
    ModalSelectComponent,
    TextSizeFilterPipe,
    AfterValueChangedDirective,
    TextSizeFilterWhitespacePipe,
    UsersWithEmailPipe,
    GetControllersForRoomsOnFloorPipe,
    AlarmSubscriptionFilterPipe,
    CustomTranslatePipe,
    EditLocationModalComponent,
    HasEveryAccessGuestRoomPermissionPipe,
    HasEveryAccessGuestRoomPermissionInRolePipe,
    HasPermissionWithIdPipe,
    GetObjectsPipe,
    GetSubobjectsPipe,
    FilterGroupsForSubobjectPipe,
    GetUserForCardPipe,
    GetCardTypeCodePipe,
    GetCardTypeNameByCodePipe,
    AccessLogsComponent,
    DateTimeFromToComponent,
    AuditComponent,
    AlarmLogsComponent,
    ControllerIsInListPipe,
    FindPropertyByCodePipe,
    PropertiesAreActivePipe,
    StatusIsActivePipe,
    RoomLogPaginatorComponent,
    TransformFloorsPipe,
    NotFoundComponent,
    FindPropertyPipe,
    GetActivePropsPipe,
    AccessControlCardComponent,
    RoomModalComponent,
    // RoomModalFacilityComponent,
    RoomModalServiceComponent,
    RoomModalLogComponent,
    RoomModalCardsComponent,
    RoomModalSettingsComponent,
    RoomModalViewComponent,
    PropIsActivePipe,
    BooleanInputComponent,
    EditControllerComponent,
    GetRoomsOnFloorPipe,
    GetRoomStatusColorPipe,
    GetFloorsForEditingGroupsPipe,
    GetFirstControllerWithPropertyPipe,
    SelectTimeComponent,
    SelectTimeCommonRoomComponent,
    FilterByControllerType,
    IOCommonAreaCardComponent,
    HvacCommonAreaCardComponent,
    IOCommonAreaModalSettingsComponent,
    IOCommonAreaModalLogComponent,
    AccessControlModalCardsComponent,
    AccessControlModalLogComponent,
    AccessControlModalSettingsComponent,
    FilterControllerByObjectPipe,
    SubObjectHaveLocations,
    HvacCommonAreaModalViewComponent,
    HvacCommonAreaModalLogComponent,
    OutputModeCopyPasteComponent,
    UserHasPermissionPipe,
    DisplayPropertyComponent,
    StickyHeaderDirectiveRooms,
    StickyHeaderDirectiveCommonArea,
    FilterByGroupTypePipe,
    GroupSettingsComponent,
    HvacModeModalComponent,
    ShowSubobjectPipe,
    FilterHvacModesPipe,
    GetRolePipe,
    GetObjectsByIdsPipe,
    SearchByNamePipe,
    SelectDaysModalComponent,
    TimeConditionComponent,
    GetFloorsForControllerSelectionPipe,
    GetAccessControllsOnFloorPipe,
    GetFloorsForRoomSelectionPipe,
    GetLocationGroupsByTypePipe,
    GetRoomsOnFloorForSelectionPipe,
    GetPresetNamePipe,
    PropExistInControllerPipe,
    PropExistInControllersPipe,
    FilterLocationsWithPropertyPipe,
    GetFloorsInSubobjectPipe,
    CanIntervalBeSetPipe,
    OutputModeForLightIsControlPipe,
    IsAccessControlPipe,
    IsPermissionAccessControl,
    UserHavePermissionForAnyAlarmsPipe,
    IsIoCommonAreaPipe,
    IsHvacCommonAreaPipe,
    IsGuestRoomPipe,
    DateTimeInlineComponent,
    ModalViewBooleanPropComponent,
    ModalViewNumericPropComponent,
    GetLatestHighestPriorityAlarmPipe,
    ButtonsSelectComponent,
    GetRoomTemperatureDataPipe,
    RoomTempInfoComponent,
    GetAlarmsLevelPipe,
    GetUnconfirmedAlarmTextPipe,
    GetValidToCssClassPipe,
    RoomTagPopoverComponent,
    TranslateValuePipe,
    TextIncludePipe,
    NumberInputCodeComponent,
    SortByAdminPropsPipe,
    ModalViewMutexBooleanPropComponent,
    PropIsLoadingPipe,
    PropIsHvacPipe,
    RetroModeControllerPositionPipe,
    RoomHaveBathroomPipe,
    FloorHaveAcLocations,
    FloorHasAcLocationOrPassageRelayPipe,
    GetLocationsInSubObjectPipe,
    GetControllersInSubObjectPipe,
    ConvertLoadingComponent,
    GetValidFromToCssPipe,
    LocationHasTagPipe,
    CommonAreasExistInProject,
    IncludesPipe,
    BleKeyQrCodeComponent,
    RoomModalHvacComponent,
    GetFancoilSpeedPropsPipe,
    GetControllerByIndexPipe,
    GetRoomRadiatorStatusPipe,
    GetBathroomRadiatorStatusPipe,
    GetRelayOutputStatusNamePipe,
    GetRelayOutputStatusNumberPipe,
    GetRelayOutputStatusStatePipe,
    GetRelayOutputByNumberPipe,
    ShowRadiatorPipe,
    ShowRadiatorModeSelectorPipe,
    GetRelayOutputNumbersPipe,
    ShowRelayOutputSelectorPipe,
    GetAllRelayPropsFromControllerPipe,
    IsBurglaryDetectedPropPipe,
    GetBurglaryIncludeNamePipe,
    ModalViewMultipleBooleanPropComponent,
    GetTypeForPresetPropPipe,
    GetSubtitleForPresetPropsPipe,
    ShowDisplayPropPipe,
    AllPropsComponent,
    ApplyToModalComponent,
    ControllerNotPolledPipe,
    GetDictionaryValuePipe,
    DisplayDictionaryPropInRoomOverviewComponent,
    DisplayDictionaryPropInRoomModalComponent,
    GetRelayOutputNamePipe,
    GetRelayOutputPropsOfSameTypePipe,
    HideHumidityAndCo2Pipe,
    FilterTemperatureLogsPipe,
    SortFanCoilSpeedPropsPipe,
    GetLogValuePropsPipe,
    GetLogTemperaturePropsPipe,
    RunFunctionPipe,
    GetLogValuePropertyNamePipe,
    GetControllersInObjectPipe,
    GetAveragePropValuePipe,
    AllControllersInLocNotPollingPipe,
    ModalViewHeatCoolAutoComponent,
    DisplaySetTemperatureComponent,
    ModalViewSetTemperatureComponent,
    EnterRoomNumberModalComponent,
    LocationDragSelectModalComponent,
    SearchLocationAndController,
    ShowHvacColumnPipe,
    SortByNamePipe,
    ModalViewWindowPropComponent,
    ModalViewFancoilModeComponent,
    DisplayWindowComponent,
    GetSelectedControllersPipe,
    UpdatingComponent,
    HealthCheckModalComponent,
    ReversedCommunicationAccessControlExistsInProjectPipe,
    DisplayFancoilModeInRoomOverviewComponent,
    RelayOutputsListComponent,
    FilterHiddenPipe
  ],
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    OwlDateTimeModule,
    OwlNativeDateTimeModule,
    FormsModule,
    IonicModule,
    NgxChartsModule,
    DragToSelectModule,
    NgApexchartsModule,
    TranslateModule.forChild({extend: true})
  ],
  exports: [
    TranslateModule,
    HeaderComponent,
    FooterComponent,
    SidebarComponent,
    MessageBoxComponent,
    ModalConfirmationComponent,
    ModalSelectComponent,
    FilterComponent,
    FilterSelectComponent,
    PaginatorComponent,
    UsersFilterPipe,
    TextSizeFilterPipe,
    AfterValueChangedDirective,
    TextSizeFilterWhitespacePipe,
    UsersWithEmailPipe,
    IonicModule,
    GetControllersForRoomsOnFloorPipe,
    NgxChartsModule,
    AlarmSubscriptionFilterPipe,
    CustomTranslatePipe,
    EditLocationModalComponent,
    HasEveryAccessGuestRoomPermissionPipe,
    HasEveryAccessGuestRoomPermissionInRolePipe,
    HasPermissionWithIdPipe,
    GetObjectsPipe,
    GetSubobjectsPipe,
    FilterGroupsForSubobjectPipe,
    GetUserForCardPipe,
    GetCardTypeCodePipe,
    GetCardTypeNameByCodePipe,
    AccessLogsComponent,
    DateTimeFromToComponent,
    AuditComponent,
    AlarmLogsComponent,
    ControllerIsInListPipe,
    FindPropertyByCodePipe,
    PropertiesAreActivePipe,
    StatusIsActivePipe,
    RoomLogPaginatorComponent,
    OwlDateTimeModule,
    OwlNativeDateTimeModule,
    TransformFloorsPipe,
    NotFoundComponent,
    FindPropertyPipe,
    GetActivePropsPipe,
    AccessControlCardComponent,
    RoomModalComponent,
    // RoomModalFacilityComponent,
    RoomModalServiceComponent,
    RoomModalLogComponent,
    RoomModalCardsComponent,
    RoomModalSettingsComponent,
    RoomModalViewComponent,
    PropIsActivePipe,
    BooleanInputComponent,
    EditControllerComponent,
    GetRoomsOnFloorPipe,
    GetRoomStatusColorPipe,
    GetFloorsForEditingGroupsPipe,
    GetFirstControllerWithPropertyPipe,
    SelectTimeComponent,
    SelectTimeCommonRoomComponent,
    FilterByControllerType,
    IOCommonAreaCardComponent,
    HvacCommonAreaCardComponent,
    IOCommonAreaModalSettingsComponent,
    IOCommonAreaModalLogComponent,
    AccessControlModalCardsComponent,
    AccessControlModalLogComponent,
    AccessControlModalSettingsComponent,
    FilterControllerByObjectPipe,
    SubObjectHaveLocations,
    HvacCommonAreaModalViewComponent,
    HvacCommonAreaModalLogComponent,
    UserHasPermissionPipe,
    DisplayPropertyComponent,
    StickyHeaderDirectiveRooms,
    StickyHeaderDirectiveCommonArea,
    FilterByGroupTypePipe,
    GroupSettingsComponent,
    HvacModeModalComponent,
    ShowSubobjectPipe,
    FilterHvacModesPipe,
    GetRolePipe,
    GetObjectsByIdsPipe,
    SearchByNamePipe,
    SelectDaysModalComponent,
    TimeConditionComponent,
    GetFloorsForControllerSelectionPipe,
    GetAccessControllsOnFloorPipe,
    GetFloorsForRoomSelectionPipe,
    GetLocationGroupsByTypePipe,
    GetRoomsOnFloorForSelectionPipe,
    GetPresetNamePipe,
    PropExistInControllerPipe,
    PropExistInControllersPipe,
    FilterLocationsWithPropertyPipe,
    GetFloorsInSubobjectPipe,
    CanIntervalBeSetPipe,
    OutputModeForLightIsControlPipe,
    IsAccessControlPipe,
    IsPermissionAccessControl,
    UserHavePermissionForAnyAlarmsPipe,
    IsIoCommonAreaPipe,
    IsHvacCommonAreaPipe,
    IsGuestRoomPipe,
    GetLatestHighestPriorityAlarmPipe,
    GetValidToCssClassPipe,
    TextIncludePipe,
    PropIsHvacPipe,
    RetroModeControllerPositionPipe,
    RoomHaveBathroomPipe,
    FloorHaveAcLocations,
    FloorHasAcLocationOrPassageRelayPipe,
    GetLocationsInSubObjectPipe,
    GetControllersInSubObjectPipe,
    CommonAreasExistInProject,
    GetControllerByIndexPipe,
    GetAllRelayPropsFromControllerPipe,
    IsBurglaryDetectedPropPipe,
    GetBurglaryIncludeNamePipe,
    GetTypeForPresetPropPipe,
    GetSubtitleForPresetPropsPipe,
    ShowDisplayPropPipe,
    GetDictionaryValuePipe,
    GetRelayOutputPropsOfSameTypePipe,
    SortFanCoilSpeedPropsPipe,
    GetLogValuePropsPipe,
    GetLogTemperaturePropsPipe,
    RunFunctionPipe,
    NgApexchartsModule,
    GetLogValuePropertyNamePipe,
    GetControllersInObjectPipe,
    GetAveragePropValuePipe,
    AllControllersInLocNotPollingPipe,
    ModalViewHeatCoolAutoComponent,
    DisplaySetTemperatureComponent,
    ModalViewSetTemperatureComponent,
    EnterRoomNumberModalComponent,
    LocationDragSelectModalComponent,
    SearchLocationAndController,
    ShowHvacColumnPipe,
    SortByNamePipe,
    ModalViewWindowPropComponent,
    ModalViewFancoilModeComponent,
    DisplayWindowComponent,
    GetSelectedControllersPipe,
    HealthCheckModalComponent,
    ReversedCommunicationAccessControlExistsInProjectPipe,
    DisplayFancoilModeInRoomOverviewComponent,
    RelayOutputsListComponent,
    FilterHiddenPipe
  ],
  providers: [
    {provide: OWL_DATE_TIME_LOCALE, useValue: (JSON.parse(localStorage.getItem('irooms_user_language'))?.language==='hr'?'hr-HR':'en-UK')},
    StatusIsActivePipe,
    PropertiesAreActivePipe,
    FindPropertyByCodePipe,
    CustomTranslatePipe,
    HasEveryAccessGuestRoomPermissionPipe,
    GetControllerByIndexPipe,
  ]
})
export class SharedModule { }
